import type { Atmosphere } from '../game/store';

/**
 * Procedural audio: every sound is synthesized with Web Audio, no files.
 * Sensor sounds are always centered (mono) so they never give away direction.
 */
type Ctx = AudioContext & { master: GainNode; dread: GainNode; noise: AudioBuffer; curve: Float32Array };

const ATMOS_LEVEL: Record<Atmosphere, number> = { normal: 1, subtle: 0.55, off: 0 };
const VOWELS: readonly (readonly [number, number])[] = [
  [300, 870],
  [400, 2000],
  [600, 1000],
  [700, 1200],
  [300, 2300],
  [500, 1500],
];

type DreadKind =
  | 'whisper' | 'phantomDoor' | 'groan' | 'wind' | 'knocks' | 'breath' | 'laugh'
  // Rare events: each far less likely than the everyday sounds above.
  | 'musicBox' | 'visorTap' | 'scratching' | 'metalDrag' | 'drip' | 'bell' | 'clock' | 'chains' | 'party' | 'choir';
export type { DreadKind };
const DREAD_WEIGHTS: readonly [DreadKind, number][] = [
  ['whisper', 22],
  ['phantomDoor', 22],
  ['groan', 18],
  ['wind', 15],
  ['knocks', 12],
  ['breath', 8],
  ['laugh', 3],
  // About one dread event in eight is one of these, so a given rare sound turns up only every
  // several matches. None uses words, numbers or directions, or resembles a gameplay sound.
  ['musicBox', 1.2],
  ['visorTap', 1.4],
  ['scratching', 1.4],
  ['metalDrag', 1.4],
  ['drip', 1.4],
  ['bell', 1],
  ['clock', 1.2],
  ['chains', 1.2],
  ['party', 0.9],
  ['choir', 0.9],
];

class AudioEngine {
  private ctx: Ctx | null = null;
  private soundOn = true;
  private atmosphere: Atmosphere = 'normal';
  private nextDreadAt = 0;
  private lastDread: DreadKind | null = null;

  /** Must be called from a user gesture (browsers block audio until then). */
  unlock(): void {
    if (!this.ctx) this.ctx = this.create();
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setSound(on: boolean): void {
    this.soundOn = on;
    if (this.ctx) this.ctx.master.gain.value = on ? 0.9 : 0;
  }

  setAtmosphere(level: Atmosphere): void {
    this.atmosphere = level;
    if (this.ctx) this.ctx.dread.gain.value = ATMOS_LEVEL[level];
  }

  private create(): Ctx | null {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC() as Ctx;
    ctx.master = ctx.createGain();
    ctx.master.gain.value = this.soundOn ? 0.9 : 0;
    ctx.master.connect(ctx.destination);
    ctx.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = ctx.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;

    // Ambient drone: detuned low saws under a slowly sweeping lowpass.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 240;
    const dg = ctx.createGain();
    dg.gain.value = 0.03;
    lp.connect(dg).connect(ctx.master);
    for (const f of [55, 55.35, 82.4]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.connect(lp);
      o.start();
    }
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lg = ctx.createGain();
    lg.gain.value = 90;
    lfo.connect(lg).connect(lp.frequency);
    lfo.start();

    // Dread bus: its own level plus a dark, distant reverb.
    ctx.dread = ctx.createGain();
    ctx.dread.gain.value = ATMOS_LEVEL[this.atmosphere];
    const len = Math.floor(ctx.sampleRate * 3.2);
    const ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      for (let n = 0; n < len; n++) data[n] = (Math.random() * 2 - 1) * Math.pow(1 - n / len, 2.6);
    }
    const conv = ctx.createConvolver();
    conv.buffer = ir;
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    const dry = ctx.createGain();
    dry.gain.value = 0.6;
    const dark = ctx.createBiquadFilter();
    dark.type = 'lowpass';
    dark.frequency.value = 3200;
    ctx.dread.connect(dark);
    dark.connect(dry).connect(ctx.master);
    dark.connect(conv).connect(wet).connect(ctx.master);
    ctx.curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) ctx.curve[i] = Math.tanh((i / 128 - 1) * 3);
    return ctx;
  }

  private live(): Ctx | null {
    const c = this.ctx;
    return c && this.soundOn && c.state === 'running' ? c : null;
  }

  private env(g: GainNode, t: number, peak: number, att: number, dec: number): void {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + att);
    g.gain.exponentialRampToValueAtTime(0.0001, t + att + dec);
  }

  private tone(type: OscillatorType, freq: number, t: number, peak: number, att: number, dec: number, dest?: AudioNode): void {
    const c = this.ctx!;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = c.createGain();
    this.env(g, t, peak, att, dec);
    o.connect(g).connect(dest ?? c.master);
    o.start(t);
    o.stop(t + att + dec + 0.02);
  }

  private noiseBurst(t: number, dur: number, filter: BiquadFilterType, freq: number, q: number, peak: number, att: number, dest?: AudioNode): void {
    const c = this.ctx!;
    const s = c.createBufferSource();
    s.buffer = c.noise;
    const f = c.createBiquadFilter();
    f.type = filter;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = c.createGain();
    this.env(g, t, peak, att, dur);
    s.connect(f).connect(g).connect(dest ?? c.master);
    s.start(t, Math.random() * 0.5, dur + att + 0.05);
  }

  // ---------- gameplay sounds ----------

  /** Door seal: short, and different every time so repetition never grates. */
  hiss(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    this.noiseBurst(t, 0.13 + Math.random() * 0.05, 'bandpass', 2400 + Math.random() * 1800, 0.7 + Math.random() * 0.6, 0.16 * (0.8 + Math.random() * 0.35), 0.006);
    const o = c.createOscillator();
    o.frequency.setValueAtTime(130 + Math.random() * 30, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 0.08);
    const g = c.createGain();
    this.env(g, t, 0.12, 0.004, 0.08);
    o.connect(g).connect(c.master);
    o.start(t);
    o.stop(t + 0.12);
  }

  servo(durationMs: number, base: number): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const d = durationMs / 1000;
    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(base, t);
    o.frequency.linearRampToValueAtTime(base * 1.35, t + d);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.03, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g).connect(c.master);
    o.start(t);
    o.stop(t + d + 0.02);
  }

  thud(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const o = c.createOscillator();
    o.frequency.setValueAtTime(75, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.22);
    const g = c.createGain();
    this.env(g, t, 0.45, 0.005, 0.25);
    o.connect(g).connect(c.master);
    o.start(t);
    o.stop(t + 0.3);
    this.noiseBurst(t, 0.12, 'lowpass', 400, 0.7, 0.2, 0.003);
  }

  tick(): void {
    const c = this.live();
    if (c) this.tone('square', 1600, c.currentTime, 0.025, 0.002, 0.03);
  }

  swap(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    [0, 0.26, 0.52].forEach((dt, i) => this.tone('triangle', 320 + i * 110, t + dt, 0.05, 0.004, 0.12));
    this.tone('sine', 520, t + 0.78, 0.08, 0.003, 0.1);
  }

  shimmer(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const s = c.createBufferSource();
    s.buffer = c.noise;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 6;
    f.frequency.setValueAtTime(5500, t);
    f.frequency.exponentialRampToValueAtTime(1200, t + 1);
    const g = c.createGain();
    this.env(g, t, 0.12, 0.03, 0.95);
    s.connect(f).connect(g).connect(c.master);
    s.start(t, 0, 1.05);
  }

  coreChime(band: 'faint' | 'warm' | 'hot'): void {
    const c = this.live();
    if (c) this.tone('sine', band === 'hot' ? 1175 : band === 'warm' ? 880 : 660, c.currentTime, 0.07, 0.005, 0.35);
  }

  threatBeep(band: 'detected' | 'near' | 'danger'): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const o = c.createOscillator();
    o.type = 'square';
    o.frequency.value = band === 'danger' ? 330 : band === 'near' ? 247 : 196;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900;
    const g = c.createGain();
    this.env(g, t, 0.06, 0.004, 0.14);
    o.connect(lp).connect(g).connect(c.master);
    o.start(t);
    o.stop(t + 0.2);
  }

  /** EMP discharge: a rising whine snapping into a crackle. */
  empFire(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const o = c.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(1800, t + 0.22);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400;
    const g = c.createGain();
    this.env(g, t, 0.07, 0.02, 0.22);
    o.connect(lp).connect(g).connect(c.master);
    o.start(t);
    o.stop(t + 0.28);
    this.noiseBurst(t + 0.22, 0.25, 'highpass', 3000, 0.7, 0.14, 0.004);
  }

  /** Your sensors filling with static after a rival EMP. Centred, like every sensor sound. */
  jammed(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    this.noiseBurst(t, 1.2, 'bandpass', 1500, 0.4, 0.12, 0.01);
    this.tone('square', 90, t, 0.05, 0.005, 0.5);
  }

  /** A bulkhead slamming shut: heavy clank plus a hydraulic lock. */
  seal(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    const o = c.createOscillator();
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.3);
    const g = c.createGain();
    this.env(g, t, 0.5, 0.004, 0.35);
    o.connect(g).connect(c.master);
    o.start(t);
    o.stop(t + 0.4);
    this.tone('triangle', 640, t + 0.02, 0.06, 0.002, 0.3);
    this.tone('triangle', 905, t + 0.02, 0.035, 0.002, 0.25);
    this.noiseBurst(t + 0.12, 0.2, 'bandpass', 1800, 1.2, 0.08, 0.01);
  }

  pickup(): void {
    const c = this.live();
    if (!c) return;
    const t = c.currentTime;
    this.tone('sine', 740, t, 0.07, 0.004, 0.15);
    this.tone('sine', 1110, t + 0.08, 0.07, 0.004, 0.25);
  }

  fanfare(): void {
    const c = this.live();
    if (c) [523, 659, 784, 1046].forEach((f, i) => this.tone('triangle', f, c.currentTime + i * 0.11, 0.12, 0.01, 0.5));
  }

  defeat(): void {
    const c = this.live();
    if (!c) return;
    [220, 185, 147, 110].forEach((f, i) => {
      const t = c.currentTime + i * 0.16;
      const o = c.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const lp = c.createBiquadFilter();
      lp.frequency.value = 700;
      const g = c.createGain();
      this.env(g, t, 0.1, 0.01, 0.6);
      o.connect(lp).connect(g).connect(c.master);
      o.start(t);
      o.stop(t + 0.7);
    });
  }

  // ---------- dread layer ----------
  // Dread sounds never carry information: they are not tied to any position, panning is
  // random, and their only link to play is frequency rising with bands the player already sees.

  resetDread(): void {
    this.nextDreadAt = 0;
  }

  /** Call every frame while playing; `tension` is 1 (calm) down to ~0.55 (danger close). */
  dreadTick(nowMs: number, tension: number): void {
    if (!this.ctx) return;
    if (!this.nextDreadAt) {
      this.nextDreadAt = nowMs + (45 + Math.random() * 30) * 1000 * tension;
      return;
    }
    if (nowMs < this.nextDreadAt) return;
    this.playDread();
    this.nextDreadAt = nowMs + Math.max(20, (60 + Math.random() * 60) * tension) * 1000;
  }

  playDread(kind?: DreadKind): void {
    const c = this.live();
    if (!c || this.atmosphere === 'off') return;
    const k = kind ?? this.pickDread();
    this.lastDread = k;
    try {
      this[k](c);
    } catch {
      /* a failed atmosphere sound must never interrupt play */
    }
  }

  private pickDread(): DreadKind {
    const options = DREAD_WEIGHTS.filter(([k]) => k !== this.lastDread);
    let r = Math.random() * options.reduce((s, [, w]) => s + w, 0);
    for (const [k, w] of options) if ((r -= w) < 0) return k;
    return 'groan';
  }

  private pan(c: Ctx, spread = 0.8): StereoPannerNode {
    const p = c.createStereoPanner();
    p.pan.value = (Math.random() * 2 - 1) * spread;
    p.connect(c.dread);
    return p;
  }

  private radio(c: Ctx, dest: AudioNode): AudioNode {
    const hp = c.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 420;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2900;
    const ws = c.createWaveShaper();
    ws.curve = c.curve;
    hp.connect(lp).connect(ws).connect(dest);
    return hp;
  }

  private staticBed(c: Ctx, dest: AudioNode, t: number, dur: number, level: number): void {
    const s = c.createBufferSource();
    s.buffer = c.noise;
    s.loop = true;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 0.5;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(level, t + 0.08);
    g.gain.setValueAtTime(level, t + dur - 0.15);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(bp).connect(g).connect(dest);
    s.start(t, Math.random() * 0.5);
    s.stop(t + dur + 0.05);
  }

  private syllable(c: Ctx, dest: AudioNode, t: number, dur: number): void {
    const v = VOWELS[Math.floor(Math.random() * VOWELS.length)]!;
    const s = c.createBufferSource();
    s.buffer = c.noise;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(1, t + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(g);
    v.forEach((f, i) => {
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = f * (0.92 + Math.random() * 0.16);
      bp.Q.value = 9;
      const fg = c.createGain();
      fg.gain.value = i ? 0.5 : 1;
      g.connect(bp).connect(fg).connect(dest);
    });
    s.start(t, Math.random() * 0.6, dur + 0.05);
  }

  private laughSyllable(c: Ctx, dest: AudioNode, t: number, dur: number, f0: number, formants: readonly number[], peak: number, drop: number): void {
    const o = c.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * drop, t + dur);
    const vib = c.createOscillator();
    vib.frequency.value = 5.5;
    const vg = c.createGain();
    vg.gain.value = f0 * 0.025;
    vib.connect(vg).connect(o.frequency);
    const h = c.createBufferSource();
    h.buffer = c.noise;
    const hg = c.createGain();
    hg.gain.setValueAtTime(0.0001, t);
    hg.gain.exponentialRampToValueAtTime(0.6, t + 0.02);
    hg.gain.exponentialRampToValueAtTime(0.08, t + dur * 0.4);
    hg.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + Math.min(0.05, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(peak * 0.5, t + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    h.connect(hg).connect(g);
    formants.forEach((fr, i) => {
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = fr;
      bp.Q.value = 5;
      const fg = c.createGain();
      fg.gain.value = i ? 0.55 : 1;
      g.connect(bp).connect(fg).connect(dest);
    });
    const body = c.createBiquadFilter();
    body.type = 'lowpass';
    body.frequency.value = 420;
    const bg = c.createGain();
    bg.gain.value = 0.5;
    g.connect(body).connect(bg).connect(dest);
    o.start(t);
    o.stop(t + dur + 0.03);
    vib.start(t);
    vib.stop(t + dur + 0.03);
    h.start(t, Math.random() * 0.6, dur + 0.05);
  }

  private whisper(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const out = c.createGain();
    out.gain.value = 0.11;
    out.connect(this.pan(c, 0.5));
    const r = this.radio(c, out);
    let tt = t;
    const n = 3 + Math.floor(Math.random() * 4);
    for (let i = 0; i < n; i++) {
      const d = 0.12 + Math.random() * 0.16;
      this.syllable(c, r, tt, d);
      tt += d + Math.random() * 0.09;
    }
    this.staticBed(c, out, t - 0.03, tt - t + 0.25, 0.05);
  }

  private phantomDoor(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 650;
    lp.connect(this.pan(c));
    this.noiseBurst(t, 0.16, 'bandpass', 2000 + Math.random() * 1200, 1, 0.18, 0.008, lp);
    const o = c.createOscillator();
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(60, t + 0.1);
    const g = c.createGain();
    this.env(g, t, 0.2, 0.004, 0.1);
    o.connect(g).connect(lp);
    o.start(t);
    o.stop(t + 0.15);
  }

  private groan(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const dur = 2.4 + Math.random() * 1.2;
    const p = this.pan(c, 0.6);
    const o = c.createOscillator();
    o.type = 'sawtooth';
    const f0 = 38 + Math.random() * 16;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.linearRampToValueAtTime(f0 * (0.8 + Math.random() * 0.5), t + dur);
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 7;
    bp.frequency.setValueAtTime(240, t);
    bp.frequency.exponentialRampToValueAtTime(110, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.32, t + dur * 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(bp).connect(g).connect(p);
    o.start(t);
    o.stop(t + dur + 0.05);
    const ticks = 2 + Math.floor(Math.random() * 3);
    for (let k = 0; k < ticks; k++) this.tone('sine', 1800 + Math.random() * 1600, t + dur * 0.5 + Math.random() * dur * 0.8, 0.03, 0.002, 0.25, p);
  }

  private wind(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const dur = 3.5 + Math.random() * 2;
    const p = this.pan(c, 0.9);
    const s = c.createBufferSource();
    s.buffer = c.noise;
    s.loop = true;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 4;
    bp.frequency.setValueAtTime(260, t);
    bp.frequency.exponentialRampToValueAtTime(620 + Math.random() * 300, t + dur * 0.45);
    bp.frequency.exponentialRampToValueAtTime(220, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.22, t + dur * 0.45);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(bp).connect(g).connect(p);
    s.start(t);
    s.stop(t + dur + 0.05);
    p.pan.setValueAtTime(p.pan.value, t);
    p.pan.linearRampToValueAtTime(-p.pan.value, t + dur);
  }

  private knocks(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 520;
    lp.connect(this.pan(c));
    const gap = 0.45 + Math.random() * 0.25;
    for (let k = 0; k < 3; k++) {
      const tt = t + k * gap;
      const o = c.createOscillator();
      o.frequency.setValueAtTime(140, tt);
      o.frequency.exponentialRampToValueAtTime(70, tt + 0.08);
      const g = c.createGain();
      this.env(g, tt, 0.35, 0.002, 0.12);
      o.connect(g).connect(lp);
      o.start(tt);
      o.stop(tt + 0.16);
      this.noiseBurst(tt, 0.05, 'lowpass', 2000, 0.7, 0.25, 0.001, lp);
    }
  }

  private breath(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const p = this.pan(c, 0.25);
    const s = c.createBufferSource();
    s.buffer = c.noise;
    s.loop = true;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 850 + Math.random() * 300;
    bp.Q.value = 1.2;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.8);
    g.gain.exponentialRampToValueAtTime(0.002, t + 1.05);
    g.gain.exponentialRampToValueAtTime(0.12, t + 1.35);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
    bp.frequency.setValueAtTime(bp.frequency.value, t + 1.05);
    bp.frequency.exponentialRampToValueAtTime(600, t + 2.4);
    s.connect(bp).connect(g).connect(p);
    s.start(t);
    s.stop(t + 2.5);
  }

  /**
   * Placeholder for a recorded voice-actor laugh: classic horror-host style (deep, slow,
   * savoring), an original performance, never an impression of a real person.
   */
  private laugh(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const out = c.createGain();
    out.gain.value = 0.085;
    out.connect(this.pan(c, 0.35));
    const r = this.radio(c, out);
    const base = 92 + Math.random() * 14;
    let tt = t;
    for (let a = 0; a < 2; a++) {
      this.laughSyllable(c, r, tt, 0.14, base * 0.9, [480, 880], 0.35, 0.94);
      tt += 0.32;
    }
    tt += 0.12;
    const n = 5 + Math.floor(Math.random() * 2);
    for (let i = 0; i < n; i++) {
      const shape = Math.sin(Math.PI * Math.min(1, (i / (n - 1)) * 1.15));
      const d = 0.17 + 0.05 * shape;
      this.laughSyllable(c, r, tt, d, base * (1.05 + 0.35 * shape), i % 2 ? [650, 1080] : [700, 1150], 0.45 + 0.55 * shape, 0.9);
      tt += d + 0.07;
    }
    this.laughSyllable(c, r, tt + 0.04, 0.95, base * 1.15, [560, 950], 0.8, 0.62);
    tt += 1.05;
    this.staticBed(c, out, t - 0.05, tt - t + 0.35, 0.045);
  }

  // ---------- rare dread events ----------

  /** A few notes of an original, slightly out-of-tune melody that winds down mid-phrase. */
  private musicBox(c: Ctx): void {
    const p = this.pan(c, 0.5);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3800;
    lp.connect(p);
    // Natural minor degrees around A5; a fresh short phrase each time.
    const scale = [0, 2, 3, 5, 7, 8, 10, 12];
    const root = 880 * (0.985 + Math.random() * 0.03);
    const n = 7 + Math.floor(Math.random() * 5);
    let tt = c.currentTime + 0.05;
    let gap = 0.34;
    let step = 4;
    for (let i = 0; i < n; i++) {
      step = Math.max(0, Math.min(scale.length - 1, step + Math.floor(Math.random() * 5) - 2));
      const f = root * 2 ** (scale[step]! / 12) * (1 + (Math.random() - 0.5) * 0.012);
      this.tone('sine', f, tt, 0.09, 0.003, 0.9, lp);
      this.tone('triangle', f * 2.01, tt, 0.025, 0.002, 0.35, lp);
      tt += gap;
      gap *= 1.07 + Math.random() * 0.05; // the spring running down
    }
  }

  /** Two or three soft taps on the outside of your own visor glass. */
  private visorTap(c: Ctx): void {
    const p = this.pan(c, 0.15);
    const taps = 2 + Math.floor(Math.random() * 2);
    let tt = c.currentTime + 0.05;
    for (let k = 0; k < taps; k++) {
      this.noiseBurst(tt, 0.02, 'bandpass', 3200, 2, 0.2, 0.001, p);
      this.tone('sine', 2600 + Math.random() * 500, tt, 0.05, 0.001, 0.18, p);
      tt += 0.28 + Math.random() * 0.2;
    }
  }

  /** Slow scratching from inside a wall, then silence. */
  private scratching(c: Ctx): void {
    const p = this.pan(c, 0.7);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400; // muffled by the wall
    lp.connect(p);
    let tt = c.currentTime + 0.05;
    const strokes = 3 + Math.floor(Math.random() * 3);
    for (let k = 0; k < strokes; k++) {
      const dur = 0.25 + Math.random() * 0.35;
      const s = c.createBufferSource();
      s.buffer = c.noise;
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 3;
      bp.frequency.setValueAtTime(2600 + Math.random() * 900, tt);
      bp.frequency.linearRampToValueAtTime(1700 + Math.random() * 600, tt + dur);
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, tt);
      g.gain.exponentialRampToValueAtTime(0.16, tt + 0.04);
      // Grainy: a fast wobble in level, like a nail catching.
      const lfo = c.createOscillator();
      lfo.frequency.value = 28 + Math.random() * 20;
      const depth = c.createGain();
      depth.gain.value = 0.07;
      lfo.connect(depth).connect(g.gain);
      g.gain.setValueAtTime(0.16, tt + dur - 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, tt + dur);
      s.connect(bp).connect(g).connect(lp);
      s.start(tt, Math.random() * 0.5, dur + 0.05);
      lfo.start(tt);
      lfo.stop(tt + dur + 0.05);
      tt += dur + 0.15 + Math.random() * 0.35;
    }
  }

  /** Something heavy dragged a long way across a distant floor. */
  private metalDrag(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const dur = 4 + Math.random() * 2;
    const p = this.pan(c, 0.8);
    const s = c.createBufferSource();
    s.buffer = c.noise;
    s.loop = true;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 420;
    const o = c.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(52 + Math.random() * 20, t);
    o.frequency.linearRampToValueAtTime(44 + Math.random() * 20, t + dur);
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 180;
    bp.Q.value = 3;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.26, t + 0.6);
    g.gain.setValueAtTime(0.26, t + dur - 1.2);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(lp).connect(g);
    o.connect(bp).connect(g);
    g.connect(p);
    s.start(t);
    s.stop(t + dur + 0.05);
    o.start(t);
    o.stop(t + dur + 0.05);
    p.pan.setValueAtTime(p.pan.value, t);
    p.pan.linearRampToValueAtTime(p.pan.value * -0.6, t + dur);
  }

  /** An echoing drip that slows, then stops. */
  private drip(c: Ctx): void {
    const p = this.pan(c, 0.6);
    let tt = c.currentTime + 0.05;
    let gap = 0.7;
    const n = 6 + Math.floor(Math.random() * 4);
    for (let k = 0; k < n; k++) {
      const o = c.createOscillator();
      const f = 1100 + Math.random() * 300;
      o.frequency.setValueAtTime(f, tt);
      o.frequency.exponentialRampToValueAtTime(f * 0.45, tt + 0.06);
      const g = c.createGain();
      this.env(g, tt, 0.12, 0.002, 0.09);
      o.connect(g).connect(p);
      o.start(tt);
      o.stop(tt + 0.12);
      tt += gap;
      gap *= 1.18 + Math.random() * 0.12;
    }
  }

  /** One deep, slow toll from far outside the cube. */
  private bell(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const p = this.pan(c, 0.4);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1300;
    lp.connect(p);
    const f = 92 + Math.random() * 12;
    // Inharmonic partials give the bell its metal.
    const partials: [number, number, number][] = [
      [0.5, 0.1, 7],
      [1, 0.16, 6],
      [1.19, 0.07, 4.5],
      [2.0, 0.08, 4],
      [2.76, 0.05, 3],
      [5.4, 0.025, 1.6],
    ];
    for (const [ratio, peak, dec] of partials) this.tone('sine', f * ratio, t, peak, 0.01, dec, lp);
  }

  /** A faint ticking clock that stops abruptly. */
  private clock(c: Ctx): void {
    const p = this.pan(c, 0.5);
    let tt = c.currentTime + 0.05;
    const n = 9 + Math.floor(Math.random() * 7);
    for (let k = 0; k < n; k++) {
      this.noiseBurst(tt, 0.012, 'bandpass', k % 2 ? 2300 : 3100, 6, 0.13, 0.001, p);
      this.tone('sine', k % 2 ? 1900 : 2500, tt, 0.02, 0.001, 0.04, p);
      tt += 0.5;
    }
  }

  /** A brief rattle of chain somewhere above or below. */
  private chains(c: Ctx): void {
    const p = this.pan(c, 0.6);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5200;
    lp.connect(p);
    const t = c.currentTime + 0.05;
    const n = 14 + Math.floor(Math.random() * 10);
    for (let k = 0; k < n; k++) {
      const tt = t + Math.random() ** 1.6 * 1.3; // bunched at the start, trailing off
      this.tone('triangle', 2200 + Math.random() * 3200, tt, 0.035, 0.001, 0.09 + Math.random() * 0.12, lp);
      this.noiseBurst(tt, 0.015, 'highpass', 3000, 0.7, 0.05, 0.001, lp);
    }
  }

  /** Muffled murmur and laughter of a crowd, as if a party is happening rooms away. */
  private party(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const dur = 4 + Math.random() * 1.5;
    const out = c.createGain();
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(0.5, t + 1.4);
    out.gain.setValueAtTime(0.5, t + dur - 1.6);
    out.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 520; // through several walls
    out.connect(lp).connect(this.pan(c, 0.5));
    const voices = 5;
    for (let v = 0; v < voices; v++) {
      let tt = t + Math.random() * 0.4;
      const f0 = 95 + Math.random() * 120;
      while (tt < t + dur - 0.3) {
        const d = 0.1 + Math.random() * 0.16;
        const vowel = VOWELS[Math.floor(Math.random() * VOWELS.length)]!;
        this.laughSyllable(c, out, tt, d, f0 * (0.9 + Math.random() * 0.25), vowel, 0.12 + Math.random() * 0.12, 0.96);
        tt += d + 0.04 + Math.random() * 0.22;
      }
    }
  }

  /** A low, wordless hum of many voices holding one chord, swelling and dying away. */
  private choir(c: Ctx): void {
    const t = c.currentTime + 0.05;
    const dur = 5.5 + Math.random() * 1.5;
    const out = c.createGain();
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(0.07, t + dur * 0.45);
    out.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const f1 = c.createBiquadFilter();
    f1.type = 'bandpass';
    f1.frequency.value = 420; // a closed "mm" more than an open "ah"
    f1.Q.value = 2.5;
    const f2 = c.createBiquadFilter();
    f2.type = 'bandpass';
    f2.frequency.value = 900;
    f2.Q.value = 3;
    const sum = c.createGain();
    sum.connect(f1).connect(out);
    sum.connect(f2).connect(out);
    out.connect(this.pan(c, 0.3));
    const root = 98 * (0.97 + Math.random() * 0.06);
    for (const semis of [0, 3, 7, 12, 15]) {
      for (let d = 0; d < 2; d++) {
        const o = c.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = root * 2 ** (semis / 12) * (1 + (d - 0.5) * 0.008);
        const vib = c.createOscillator();
        vib.frequency.value = 4.5 + Math.random();
        const vd = c.createGain();
        vd.gain.value = 0.6;
        vib.connect(vd).connect(o.frequency);
        o.connect(sum);
        o.start(t);
        o.stop(t + dur + 0.05);
        vib.start(t);
        vib.stop(t + dur + 0.05);
      }
    }
  }
}

export const audio = new AudioEngine();
